/**
 * `<pg-command-palette>` — busca rápida global (Cmd+K/Ctrl+K),
 * `BACKLOG.md` Módulo 14 ("Command Palette (Cmd+K)", Alta prioridade).
 *
 * Mesmo padrão de `pg-modal`: atributo `open` controla visibilidade,
 * nunca se fecha sozinho de forma definitiva (só emite `pg-close`).
 * O ATALHO GLOBAL (Cmd+K) não é responsabilidade deste componente —
 * abrir é decisão de quem monta a aplicação (`main.ts`), o mesmo
 * princípio de `pg-modal` nunca decidir seu próprio `open` inicial.
 * Uma vez aberto, o componente É responsável por: filtro de texto
 * (substring, case-insensitive — filtragem apresentacional, não regra
 * de negócio), navegação por teclado (setas + Enter), e Escape para
 * fechar.
 *
 * Dados via propriedade JS `items` (mesmo padrão de `pg-table`).
 *
 * Eventos:
 * - `pg-select`: `detail: { value }` — item escolhido (clique ou Enter).
 * - `pg-close`: Escape, clique fora, ou após `pg-select` (a tela que
 *   consome decide se navega e fecha, ou só navega).
 *
 * Uso:
 * ```js
 * const palette = document.querySelector('pg-command-palette');
 * palette.items = [{ value: '/dashboard', label: 'Dashboard', grupo: 'Navegação' }];
 * document.addEventListener('keydown', (e) => {
 *   if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); palette.setAttribute('open', ''); }
 * });
 * palette.addEventListener('pg-select', (e) => router.navegar(e.detail.value));
 * ```
 */
export class PgCommandPalette extends HTMLElement {
    static get observedAttributes() {
        return ['open'];
    }
    root;
    _items = [];
    filtro = '';
    indiceAtivo = 0;
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    get items() {
        return this._items;
    }
    set items(valor) {
        this._items = valor;
        this.render();
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        if (this.hasAttribute('open')) {
            this.filtro = '';
            this.indiceAtivo = 0;
        }
        this.render();
    }
    itensFiltrados() {
        const termo = this.filtro.trim().toLowerCase();
        if (!termo)
            return this._items;
        return this._items.filter((item) => item.label.toLowerCase().includes(termo));
    }
    emitirFechar() {
        this.dispatchEvent(new CustomEvent('pg-close', { bubbles: true, composed: true }));
    }
    selecionar(item) {
        this.dispatchEvent(new CustomEvent('pg-select', { detail: { value: item.value }, bubbles: true, composed: true }));
    }
    render() {
        const aberto = this.hasAttribute('open');
        const itens = this.itensFiltrados();
        this.indiceAtivo = Math.min(this.indiceAtivo, Math.max(0, itens.length - 1));
        const linhas = itens
            .map((item, indice) => `
        <button class="item ${indice === this.indiceAtivo ? 'ativo' : ''}" type="button" data-indice="${indice}">
          ${item.grupo ? `<span class="grupo">${escaparHtml(item.grupo)}</span>` : ''}
          <span class="label">${escaparHtml(item.label)}</span>
        </button>`)
            .join('');
        this.root.innerHTML = `
      <style>
        :host { display: ${aberto ? 'block' : 'none'}; }
        .overlay {
          position: fixed; inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex; align-items: flex-start; justify-content: center;
          padding-top: 12vh;
          z-index: 1100;
        }
        .paleta {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-lg, 16px);
          box-shadow: var(--pg-shadow-lg, 0 12px 32px rgba(0,0,0,0.45));
          width: min(560px, 90vw);
          max-height: 60vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          font-family: var(--pg-font-familia-base, system-ui);
        }
        .busca {
          width: 100%;
          box-sizing: border-box;
          background: none;
          border: none;
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-size: var(--pg-font-size-lg, 1.125rem);
          padding: var(--pg-space-4, 16px) var(--pg-space-5, 24px);
          outline: none;
        }
        .busca::placeholder { color: var(--pg-color-texto-desabilitado, #5c626b); }
        .lista { overflow-y: auto; padding: var(--pg-space-2, 8px); }
        .item {
          display: flex;
          align-items: center;
          gap: var(--pg-space-3, 12px);
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          padding: var(--pg-space-3, 12px);
          cursor: pointer;
          font-size: var(--pg-font-size-sm, 0.875rem);
        }
        .item.ativo, .item:hover { background: var(--pg-color-bg-hover, #22262b); }
        .grupo {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-desabilitado, #5c626b);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .vazio { padding: var(--pg-space-5, 24px); text-align: center; color: var(--pg-color-texto-desabilitado, #5c626b); font-size: var(--pg-font-size-sm, 0.875rem); }
      </style>
      <div class="overlay" part="overlay">
        <div class="paleta" part="paleta" role="dialog" aria-modal="true">
          <input class="busca" type="text" placeholder="Ir para..." value="${escaparHtml(this.filtro)}" autocomplete="off" />
          <div class="lista">${itens.length > 0 ? linhas : '<p class="vazio">Nenhum resultado.</p>'}</div>
        </div>
      </div>
    `;
        if (!aberto)
            return;
        const overlay = this.root.querySelector('.overlay');
        const input = this.root.querySelector('.busca');
        overlay.addEventListener('click', (evento) => {
            if (evento.target === overlay)
                this.emitirFechar();
        });
        input.focus();
        input.setSelectionRange(this.filtro.length, this.filtro.length);
        input.addEventListener('input', () => {
            this.filtro = input.value;
            this.indiceAtivo = 0;
            this.render();
        });
        input.addEventListener('keydown', (evento) => {
            const atuais = this.itensFiltrados();
            if (evento.key === 'Escape') {
                evento.preventDefault();
                this.emitirFechar();
                return;
            }
            if (evento.key === 'ArrowDown') {
                evento.preventDefault();
                this.indiceAtivo = Math.min(this.indiceAtivo + 1, Math.max(0, atuais.length - 1));
                this.render();
                return;
            }
            if (evento.key === 'ArrowUp') {
                evento.preventDefault();
                this.indiceAtivo = Math.max(this.indiceAtivo - 1, 0);
                this.render();
                return;
            }
            if (evento.key === 'Enter') {
                evento.preventDefault();
                const escolhido = atuais[this.indiceAtivo];
                if (escolhido)
                    this.selecionar(escolhido);
            }
        });
        this.root.querySelectorAll('.item[data-indice]').forEach((botao) => {
            botao.addEventListener('click', () => {
                const item = itens[Number(botao.dataset['indice'])];
                if (item)
                    this.selecionar(item);
            });
        });
    }
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-command-palette', PgCommandPalette);
//# sourceMappingURL=pg-command-palette.js.map